<template lang="html">
<v-card class="map-filters stations-page-filters">
    <v-card-title>
        <div class="titlecloser">
            <h1 class="headline mb-2">
                Map Filters
            </h1>
            <v-btn flat icon small class="closefilters" @click="hideSidebar">
                <v-icon>close</v-icon>
            </v-btn>
        </div>

        <p class="caption">Use filters to refine the features shown on the map.</p>

        <v-container
            fluid
            v-if="filterLoaded('network')"
        >
            <v-checkbox
                label="Network"
                color="primary"
                hide-details
                v-model="networkCheckbox"
                :indeterminate="network.length !== 0 && network.length !== networkDefault().length"
            ></v-checkbox>
            <div class="fixed-height">
                <div class="h-container">
                    <div v-for="item in networkOptions">
                        <v-checkbox
                            :label="item.label"
                            :value="item.value"
                            color="primary"
                            v-model="network"
                            hide-details
                        ></v-checkbox>
                    </div>
                </div>
            </div>
        </v-container>

        <v-divider v-if="filterLoaded('network')" inset></v-divider>

        <v-container
            fluid
            v-if="filterLoaded('status')"
        >
            <p class="header-label">Status</p>
            <div class="fixed-height">
                <div class="h-container">
                    <div v-for="item in statusOptions">
                        <v-checkbox
                            :label="item.label"
                            :value="item.value"
                            color="primary"
                            v-model="status"
                            hide-details
                        ></v-checkbox>
                    </div>
                </div>
            </div>
        </v-container>

        <v-divider v-if="filterLoaded('status')" inset></v-divider>

        <v-container
            fluid
            v-if="filterLoaded('analyses')"
        >
            <v-checkbox
                label="Analyses"
                color="primary"
                hide-details
                v-model="analysesCheckbox"
                :indeterminate="analyses.length !== 0 && analyses.length !== analysesDefault().length"
            ></v-checkbox>
            <div class="fixed-height">
                <div class="h-container">
                    <div v-for="item in analysesOptions">
                        <v-checkbox
                            :label="item.label"
                            :value="item.value"
                            color="primary"
                            v-model="analyses"
                            hide-details
                        ></v-checkbox>
                    </div>
                </div>
            </div>
        </v-container>

        <v-divider v-if="filterLoaded('analyses')" inset></v-divider>

        <v-container
            fluid
            v-if="filterLoaded('area')"
        >
            <v-checkbox
                label="Area"
                color="primary"
                hide-details
                v-model="areaCheckbox"
                :indeterminate="area.length !== 0 && area.length !== areaDefault().length"
            ></v-checkbox>
            <div class="fixed-height">
                <div class="h-container">
                    <div v-for="item in areaOptions">
                        <v-checkbox
                            :label="item.label"
                            :value="item.value"
                            color="primary"
                            v-model="area"
                            hide-details
                        ></v-checkbox>
                    </div>
                </div>
            </div>
        </v-container>

        <v-divider v-if="filterLoaded('area')" inset></v-divider>

        <v-container
            fluid
            style="display: flex; flex-direction: row;"
            v-if="filterLoaded('yearMin') && filterLoaded('yearMax')"
        >
            <v-flex style="padding-right: 15px;">
                <h2 class="subheading">Year (min.)</h2>
                <v-select
                    v-if="filterLoaded('yearMin')"
                    @input="s => yearChanged('yearMin', s)"
                    v-model="yearMin"
                    :items="yearOptions"
                    item-text="label"
                    return-object
                    solo
                />
            </v-flex>

            <v-flex>
                <h2 class="subheading">Year (max.)</h2>
                <v-select
                    v-if="filterLoaded('yearMax')"
                    @input="s => yearChanged('yearMax', s)"
                    v-model="yearMax"
                    :items="yearOptions"
                    item-text="label"
                    return-object
                    solo
                />
            </v-flex>
        </v-container>

    </v-card-title>
    <v-card-actions>
        <v-btn @click="reset" flat color="secondary">Reset filters</v-btn>
    </v-card-actions>
</v-card>
</template>

<script>
import * as actions from '@/constants/actions';
import mapMixin from '@/mixins/map';
import sortByKey from '@/functions/sortByKey';

export default {
    name: 'map-filters',
    mixins: [
        mapMixin,
    ],
    props: {
        filters: Array,
    },
    data() {
        return {
            yearMin: {},
            yearMax: {},
            yearOptions: [],

            status: [],
            statusOptions: [],

            analyses: [],
            analysesOptions: [],

            area: [],
            areaOptions: [],

            network: [],
            networkOptions: [],
        };
    },
    // load options and set values
    async created() {
        const map = await this.mapPromise;

        // Load current filters when stations layer is initialized
        map.on('style.load', () => {
            const waiting = () => {
                if (map.getLayer('stations-points') === undefined) {
                    setTimeout(waiting, 25);
                } else {
                    this.setFormValues();
                }
            };
            waiting();
        });
        this.initialize();
    },
    computed: {
        analysesCheckbox: {
            get() {
                return this.analyses.length !== 0;
            },
            set() {
                if (this.analyses.length === this.analysesDefault().length) {
                    this.clearFilter('analyses');
                } else {
                    this.resetFilter('analyses');
                }
            },
        },
        networkCheckbox: {
            get() {
                return this.network.length !== 0;
            },
            set() {
                if (this.network.length === this.networkDefault().length) {
                    this.clearFilter('network');
                } else {
                    this.resetFilter('network');
                }
            },
        },
        areaCheckbox: {
            get() {
                return this.area.length !== 0;
            },
            set() {
                if (this.area.length === this.areaDefault().length) {
                    this.clearFilter('area');
                } else {
                    this.resetFilter('area');
                }
            },
        },
        // array of loaded filter names
        loadedFilters() {
            return this.$store.state.loadedFilters;
        },
        // options and ranges for filter dropdowns
        filterOptions() {
            return this.$store.state.filterOptions;
        },
        // currently-set filters
        savedSettings() {
            return this.$store.state.filterSettings;
        },
    },
    methods: {
        // used for the close button beside the card title.
        hideSidebar() {
            this.$store.dispatch(actions.SHOW_MAP_SIDEBAR, false);
        },
        /**
         * reset filters to their default settings
         */
        reset() {
            // reset locally
            if (this.filterLoaded('yearMin')) {
                this.yearMin = this.yearDefault();
            }
            if (this.filterLoaded('yearMax')) {
                this.yearMax = this.yearDefault();
            }
            if (this.filterLoaded('analyses')) {
                this.analyses = this.analysesDefault();
            }
            if (this.filterLoaded('area')) {
                this.area = this.areaDefault();
            }
            if (this.filterLoaded('status')) {
                this.status = this.statusDefault();
            }
            if (this.filterLoaded('network')) {
                this.network = this.networkDefault();
            }
            // update vuex
            this.updateStore();
        },
        resetFilter(filter) {
            this[filter] = this[`${filter}Default`]();
        },
        clearFilter(filter) {
            this[filter] = [];
        },
        /**
         * handle any special cases and update vuex
         * @param  {String}  name          filter name
         * @param  {Object|Array}  val     new filter value
         * @param  {Boolean} [update=true] whether to update vuex
         */
        filterChanged(name, val, update = true) {
            if (update) {
                this.updateStore();
            }
        },
        /**
         * adjust year filters to preserve min-max range
         * @param  {String} name filter name
         * @param  {Object} val  filter value
         */
        yearChanged(name, val) {
            // if the new value breaks the year range
            if (this.yearMin.value && this.yearMax.value // not null
                && this.yearMin.value > this.yearMax.value) {
                // adjust the other control
                if (name === 'yearMin') {
                    this.yearMax = val;
                    this.filterChanged('yearMax', val, false);
                } else {
                    this.yearMin = val;
                    this.filterChanged('yearMin', val, false);
                }
            }
            this.filterChanged(name, val);
        },
        /**
         * persist an object of filter settings to vuex
         * the map page itself will handle the actual filtering
         */
        updateStore() {
            const currentSettings = {};
            // only add if the filter isn't set to its default
            // @TODO: a testDefault method (can use to display if a filter is "set" or not)
            if (this.filters.indexOf('yearMin') !== -1 && this.yearMin.value !== this.yearDefault().value) {
                currentSettings.yearMin = this.yearMin;
            }
            if (this.filters.indexOf('yearMax') !== -1 && this.yearMax.value !== this.yearDefault().value) {
                currentSettings.yearMax = this.yearMax;
            }
            if (this.filters.indexOf('analyses') !== -1 && this.analyses.length !== this.analysesDefault().length) {
                currentSettings.analyses = this.analyses;
            }
            if (this.filters.indexOf('area') !== -1 && this.area.length !== this.areaDefault().length) {
                currentSettings.area = this.area;
            }
            if (this.filters.indexOf('status') !== -1 && this.status.length !== this.statusDefault().length) {
                currentSettings.status = this.status;
            }
            if (this.filters.indexOf('network') !== -1 && this.network.length !== this.networkDefault().length) {
                currentSettings.network = this.network;
            }
            this.$store.dispatch(actions.SET_FILTER_SETTINGS, currentSettings);
        },
        /**
         * (re-)initialize form fields
         */
        initialize() {
            this.setOptions();
            this.setFormValues();
        },
        /**
         * populate form fields with filter options
         */
        setOptions() {
            // cheap deep clone filterOptions from vuex
            // so it can be sorted
            const options = JSON.parse(JSON.stringify(this.filterOptions));
            const sortLabels = sortByKey('label');
            // sort all option values alphabetically by label
            Object.entries(options).forEach((option) => {
                const [key, val] = option;

                // don't sort certain options
                if (key === 'area' || key === 'min_year' || key === 'max_year') return;

                // sort array members
                options[key] = val.sort(sortLabels);
            });

            // set year range
            if (options.min_year && options.max_year) {
                const min = options.min_year.value;
                const max = options.max_year.value;
                const yearOptions = [this.yearDefault()];
                for (let i = min; i <= max; i += 1) {
                    yearOptions.push({ value: i, label: `${i}` });
                }
                this.yearOptions = yearOptions;
            }

            // set status options
            if (options.analyses) {
                this.analysesOptions = options.analyses;
            }

            if (options.area) {
                this.areaOptions = options.area;
            }

            // set status options
            if (options.ty) {
                this.statusOptions = options.ty;
            }

            // set network options
            if (options.net) {
                this.networkOptions = options.net;
            }
        },
        /**
         * set form feld values to current/default settings
         */
        setFormValues() {
            if (this.filterLoaded('yearMin')) {
                this.yearMin = this.getFilterValue('yearMin') || this.yearDefault();
            }
            if (this.filterLoaded('yearMax')) {
                this.yearMax = this.getFilterValue('yearMax') || this.yearDefault();
            }
            if (this.filterLoaded('status')) {
                this.status = this.getFilterValue('status') || this.statusDefault();
            }
            if (this.filterLoaded('area')) {
                this.area = this.getFilterValue('area') || this.areaDefault();
            }
            if (this.filterLoaded('analyses')) {
                this.analyses = this.getFilterValue('analyses') || this.analysesDefault();
            }
            if (this.filterLoaded('network')) {
                this.network = this.getFilterValue('network') || this.networkDefault();
            }
        },
        /**
         * return saved filter values from vuex
         * @param  {String} name         filter name
         * @return {Object|Array|null}   filter value, or null if not set in vuex
         */
        getFilterValue(name) {
            if (this.savedSettings[name]) {
                return this.savedSettings[name];
            }
            return null;
        },
        /**
         * returns true if the filter is loaded on the current page
         * @param  {String} name filter name
         * @return {Boolean}     true if loaded in vuex
         */
        filterLoaded(name) {
            return this.loadedFilters.includes(name);
        },
        // default filter values
        analysesDefault() {
            return this.filterOptions.analyses.map(e => e.value);
        },
        statusDefault() {
            return this.filterOptions.ty.map(e => e.value);
        },
        areaDefault() {
            return this.filterOptions.area.map(e => e.value);
        },
        networkDefault() {
            return this.filterOptions.net.map(e => e.value);
        },
        yearDefault() {
            return { value: null, label: 'Any' };
        },
    },
    watch: {
        network() {
            this.filterChanged('type', 'network');
        },
        status() {
            this.filterChanged('type', 'status');
        },
        analyses() {
            this.filterChanged('type', 'analyses');
        },
        area() {
            this.filterChanged('type', 'area');
        },
        // re-initialize if the list of loaded filters changes
        loadedFilters() {
            this.initialize();
        },
        // re-initialize if the filter options changes
        filterOptions() {
            this.initialize();
        },
    },
};
</script>

<style lang="scss">
.map-filters.stations-page-filters {
    .divider.divider--inset {
        margin-left: 1px;
    }
    .header-label {
        display: inline-block;
        font-size: 16px;
        line-height: 30px;
        height: 30px;
        margin: 0;
    }

    .filter-control {
        margin-bottom: 1em;
        width: 100%;
    }
    .url {
        width: 100%;
        text-align: center;
    }
    label {
        color: #000 !important;
    }

    .titlecloser {
        display:flex;
        justify-content: space-between;
        width:100%;
        .closefilters {
            top: -10px;
            right: -10px;
        }
    }

    // hack for overflowed label contents for checkboxes
    // found online on stack overflow, this should be fixed in vuetify 1.1
    // you can also find this hack in WatershedFilters.vue
    .small {
        width: 175px;
    }
    .input-group.checkbox label {
        height: auto;
        margin-top: -30px;
        padding-left: 27px;
        max-width: none;
        padding-right: .2rem;
        font-size: 1rem;
        position: static;
        white-space: normal;
    }

    .input-group.checkbox .input-group__input {
        order: -1;
    }
    // end hack

    // reduce container padding
    // to provide more space for text
    .container {
        padding-left: .25rem;
        padding-right: .25rem;
    }

    .fixed-height {
        max-height: 220px;
        overflow-y: auto;
        .h-container {
            overflow-y: hidden;
            padding-left: 14px;
        }
    }
}

</style>



// WEBPACK FOOTER //
// src/components/StationsPageFilters.vue